import Axios, {
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import {
  CREDSYSTEM_LOGIN_REFRESH_TOKEN,
  CREDSYSTEM_LOGIN_TOKEN,
  CREDSYSTEM_USER_DOCUMENT,
  CREDSYSTEM_USER_PASSWORD,
  CREDSYSTEM_USER_REFRESH_TOKEN,
  CREDSYSTEM_USER_TOKEN,
  CREDSYSTEM_VALIDATION_TOKEN,
} from '~/credsystem/storage/constants';
import { CredsystemLocalStorage } from '~/credsystem/storage/LocalStorage';
import { getCredSystemUserTokenUseCase } from '../useCases/SSOCredsystemUseCases/GetCredSystemUserTokenUseCase';
import useCredsystemAuthStore from '../stores/credsystemAuth';
import Env from '../env';

export const apiCredSystem = Axios.create({
  baseURL: `https://${Env.CREDSYSTEM_CLIENT_SESSION_API}/`,
});

export const apiCredSystemInvoices = Axios.create({
  baseURL: `https://${Env.CREDSYSTEM_API}/`,
});

const requestInterceptor = async (config: InternalAxiosRequestConfig<any>) => {
  const validationToken = await CredsystemLocalStorage.get(
    CREDSYSTEM_VALIDATION_TOKEN
  );
  const userToken = await CredsystemLocalStorage.get(CREDSYSTEM_USER_TOKEN);
  if (userToken) {
    config.headers.Authorization = `Bearer ${userToken}`;
  } else if (validationToken) {
    config.headers.Authorization = `Bearer ${validationToken}`;
  }

  return config;
};

const responseInterceptorSuccess = async (
  response: AxiosResponse<any, any>
) => {
  const document = await CredsystemLocalStorage.get(CREDSYSTEM_USER_DOCUMENT);
  const password = await CredsystemLocalStorage.get(CREDSYSTEM_USER_PASSWORD);

  const currentToken = await CredsystemLocalStorage.get(CREDSYSTEM_USER_TOKEN);
  if (!document && !password) {
    useCredsystemAuthStore.getState().clear();
    return response;
  }

  if (
    document &&
    password &&
    currentToken &&
    (response?.data.mensagem === 'Não autenticado' ||
      response.data === 'JWT invalido')
  ) {
    await CredsystemLocalStorage.multiRemove([
      CREDSYSTEM_LOGIN_TOKEN,
      CREDSYSTEM_LOGIN_REFRESH_TOKEN,
    ]);

    try {
      const loginResponse = await getCredSystemUserTokenUseCase({
        document: document,
        password: password,
      });

      if (loginResponse?.token && loginResponse?.refreshToken) {
        await CredsystemLocalStorage.multiSet([
          [CREDSYSTEM_USER_TOKEN, loginResponse.token],
          [CREDSYSTEM_USER_REFRESH_TOKEN, loginResponse.refreshToken],
        ]);

        useCredsystemAuthStore.setState((currentState) => ({
          credsystemUserInfo: {
            ...currentState.credsystemUserInfo,
            token: loginResponse.token,
            refreshToken: loginResponse.refreshToken,
          },
        }));

        Axios.defaults.headers.common.Authorization =
          'Bearer ' + loginResponse.token;
        response.config.headers.Authorization = 'Bearer ' + loginResponse.token;
        return Axios.request(response.config);
      }
    } catch (error) {
      useCredsystemAuthStore.getState().clear();
    }
  }

  return response;
};

const responseInterceptorError = async function (error: any) {
  const currentToken = await CredsystemLocalStorage.get(CREDSYSTEM_USER_TOKEN);
  const document = await CredsystemLocalStorage.get(CREDSYSTEM_USER_DOCUMENT);
  const password = await CredsystemLocalStorage.get(CREDSYSTEM_USER_PASSWORD);

  if (
    document &&
    password &&
    error.response?.status === 401 &&
    currentToken &&
    (error.response?.data.mensagem === 'Não autenticado' ||
      error.response.data === 'JWT invalido')
  ) {
    await CredsystemLocalStorage.multiRemove([
      CREDSYSTEM_LOGIN_TOKEN,
      CREDSYSTEM_LOGIN_REFRESH_TOKEN,
    ]);

    try {
      const loginResponse = await getCredSystemUserTokenUseCase({
        document: document,
        password: password,
      });

      if (loginResponse?.token && loginResponse?.refreshToken) {
        await CredsystemLocalStorage.multiSet([
          [CREDSYSTEM_USER_TOKEN, loginResponse.token],
          [CREDSYSTEM_USER_REFRESH_TOKEN, loginResponse.refreshToken],
        ]);

        useCredsystemAuthStore.setState((currentState) => ({
          credsystemUserInfo: {
            ...currentState.credsystemUserInfo,
            token: loginResponse.token,
            refreshToken: loginResponse.refreshToken,
          },
        }));

        Axios.defaults.headers.common.Authorization =
          'Bearer ' + loginResponse.token;
        error.config.headers.Authorization = 'Bearer ' + loginResponse.token;
        return Axios.request(error.config);
      }
    } catch (err) {
      useCredsystemAuthStore.getState().clear();
    }
  }

  return Promise.reject(error);
};

apiCredSystem.interceptors.request.use(requestInterceptor);
apiCredSystemInvoices.interceptors.request.use(requestInterceptor);

apiCredSystem.interceptors.response.use(
  responseInterceptorSuccess,
  responseInterceptorError
);

apiCredSystemInvoices.interceptors.response.use(
  responseInterceptorSuccess,
  responseInterceptorError
);
